import { Gauge, Zap, Activity, TrendingUp, BatteryCharging } from 'lucide-react';

const LOW_PF = 0.9;
const LOW_LF = 40;

const num = (v, d = 0) =>
  v == null ? '\u2014' : Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

const FactorBadge = ({ factor, kind }) => {
  if (!factor || factor.value == null) return <span className="text-muted-foreground text-xs">{'\u2014'}</span>;
  const v = factor.value;
  const low = kind === 'pf' ? v < LOW_PF : v < LOW_LF;
  const display = kind === 'pf' ? v.toFixed(3) : `${v}%`;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-none border px-1.5 py-0.5 text-[11px] font-medium ${
        low ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
      }`}
      title={`${factor.type === 'billed' ? 'Billed' : 'Calculated'} ${kind === 'pf' ? 'power factor' : 'load factor'}`}
      data-testid={`factor-${kind}`}
    >
      {display}
      <span className="opacity-60 text-[9px] uppercase">{factor.type === 'billed' ? 'bill' : 'calc'}</span>
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="flex items-start gap-2 px-3 py-2 border border-border bg-white">
    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-bold font-mono truncate">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  </div>
);

/**
 * Renders the deterministic Demand / Power-Quality summary stored on a bill.
 * Demand, load factor and power factor are ELECTRICITY concepts — so this card
 * only considers electric meters and is a no-op for water/gas/other commodities.
 */
const isElectricMeter = (m) =>
  /electr/i.test(m?.commodity || '') ||
  m?.billing_demand != null ||
  m?.measured_peak_demand != null ||
  (m?.power_factor && m.power_factor.value != null) ||
  !!m?.demand_charge_total;

export const DemandSummaryCard = ({ usageSummary }) => {
  const allMeters = usageSummary?.per_meter || [];
  const perMeter = allMeters.filter(isElectricMeter);
  if (!perMeter.length) return null;

  const sum = (key) => perMeter.reduce((a, m) => a + (Number(m[key]) || 0), 0);
  const maxOf = (key) => {
    const vals = perMeter.map((m) => m[key]).filter((v) => v != null).map(Number);
    return vals.length ? Math.max(...vals) : null;
  };
  const rollup = {
    meter_count: perMeter.length,
    energy_kwh: sum('energy_kwh'),
    billing_demand_max: maxOf('billing_demand'),
    demand_charge_total: sum('demand_charge_total') || 0,
    has_demand: perMeter.some(
      (m) => m.billing_demand != null || m.measured_peak_demand != null || !!m.demand_charge_total
    ),
  };

  return (
    <div className="mb-4 border border-amber-200 bg-amber-50/40" data-testid="demand-summary-card">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-200 bg-amber-100/60">
        <Gauge className="h-4 w-4 text-amber-700" />
        <h4 className="text-sm font-bold text-amber-900">Demand & Power Quality</h4>
        {rollup.has_demand ? (
          <span className="ml-auto text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Demand bill</span>
        ) : (
          <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">Energy only</span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3">
        <Stat icon={Activity} label="Meters" value={num(rollup.meter_count)} />
        <Stat icon={Zap} label="Energy" value={`${num(rollup.energy_kwh)}`} sub="kWh" />
        <Stat
          icon={TrendingUp}
          label="Max Billing Demand"
          value={rollup.billing_demand_max != null ? num(rollup.billing_demand_max, 1) : '\u2014'}
          sub={rollup.billing_demand_max != null ? 'kW' : ''}
        />
        <Stat
          icon={BatteryCharging}
          label="Demand Charges"
          value={rollup.demand_charge_total ? `$${num(rollup.demand_charge_total, 2)}` : '\u2014'}
        />
      </div>

      <div className="overflow-x-auto px-3 pb-3">
        <table className="w-full border-collapse text-xs">
          <thead className="bg-white">
            <tr className="text-left text-muted-foreground">
              <th className="border border-border p-1.5 font-medium">Meter</th>
              <th className="border border-border p-1.5 font-medium">Commodity</th>
              <th className="border border-border p-1.5 font-medium text-right">Energy</th>
              <th className="border border-border p-1.5 font-medium text-right">Billing Demand</th>
              <th className="border border-border p-1.5 font-medium text-right">Peak Demand</th>
              <th className="border border-border p-1.5 font-medium text-right">Demand $</th>
              <th className="border border-border p-1.5 font-medium text-center">Load Factor</th>
              <th className="border border-border p-1.5 font-medium text-center">Power Factor</th>
            </tr>
          </thead>
          <tbody>
            {perMeter.map((m, i) => (
              <tr key={`${m.meter_number}-${i}`} className="bg-white" data-testid={`demand-meter-row-${i}`}>
                <td className="border border-border p-1.5 font-mono">{m.meter_number || '\u2014'}</td>
                <td className="border border-border p-1.5">{m.commodity || '\u2014'}</td>
                <td className="border border-border p-1.5 text-right font-mono">
                  {m.energy_kwh != null ? `${num(m.energy_kwh)} ${m.energy_uom || ''}` : '\u2014'}
                </td>
                <td className="border border-border p-1.5 text-right font-mono">
                  {m.billing_demand != null ? `${num(m.billing_demand, 1)} ${m.demand_uom || 'kW'}` : '\u2014'}
                </td>
                <td className="border border-border p-1.5 text-right font-mono">
                  {m.measured_peak_demand != null ? `${num(m.measured_peak_demand, 1)} ${m.demand_uom || 'kW'}` : '\u2014'}
                </td>
                <td className="border border-border p-1.5 text-right font-mono">
                  {m.demand_charge_total ? `$${num(m.demand_charge_total, 2)}` : '\u2014'}
                </td>
                <td className="border border-border p-1.5 text-center">
                  <FactorBadge factor={m.load_factor} kind="lf" />
                </td>
                <td className="border border-border p-1.5 text-center">
                  <FactorBadge factor={m.power_factor} kind="pf" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
